import './styles/_recommended-products.scss';
import Swiper, { Mousewheel } from 'swiper';
import {
  fetchRebuyRecommendedProducts,
  fetchProductsById,
  restructureRestProducts,
  fetchProductRecommendations,
} from '../services/products';
import 'swiper/swiper-bundle.css';

import {
  getProductItemBody,
  attachProductItemEvents,
  renderVideos,
} from '../common/product-item';
import { createLazyAction } from '../utils/lazy-action';
import { renderPlaceholderContainers } from '../common/placeholder-loader';
import { waitObjectProperty } from '../utils/performance';
import { onDocumentReady } from '../utils/dom';
import { PRODUCT_CHANGED_EVENT } from '../common/pdp-events';

Swiper.use([Mousewheel]);

const DESKTOP_SLIDES_PER_VIEW = 4.15;
const MOBILE_SLIDES_PER_VIEW = 2.15;

const sliderOptions = {
  slidesPerView: MOBILE_SLIDES_PER_VIEW,
  freeMode: false,
  loop: true,
  spaceBetween: 5,
  mousewheel: {
    forceToAxis: true,
    sensitivity: 1,
    releaseOnEdges: true,
    thresholdDelta: 10,
    thresholdTime: 200,
  },
  breakpoints: {
    851: {
      slidesPerView: DESKTOP_SLIDES_PER_VIEW,
      freeMode: false,
      loop: true,
    },
  },
};

// Swiper's loop mode clones slides to fake an infinite carousel. With only a
// few products the clones are visible and the products appear to repeat
// (A B A B...). Only enable loop when there are clearly more products than fit
// on a desktop row; otherwise show them as a plain, non-looping carousel.
const buildSliderOptions = (productCount) => {
  const loop = productCount > Math.ceil(DESKTOP_SLIDES_PER_VIEW);
  return {
    ...sliderOptions,
    loop,
    breakpoints: {
      ...sliderOptions.breakpoints,
      851: { ...sliderOptions.breakpoints[851], loop },
    },
  };
};

const state = {
  renderedStyledWithItems: {},
  renderedRecentItems: {},
  settings: {},
  elements: {},
  viewedProducts: [],
  viewedProductsSlider: null,
  styledWithSlider: null,
  viewedProductIds: [],
  currentProductId: null,
  // The product the recommendation sliders are currently built from. Starts as
  // the product the page was loaded with and follows the colourway swap.
  recommendationProductId: null,
  reloadRecommendedSections: [],
};

const enableAutoScrolling = (sliderElement, sliderInstance) => {
  let autoScrollInterval = null;
  const SCROLL_THRESHOLD = 50;
  const SCROLL_INTERVAL = 150;
  const TRANSITION_SPEED = 1500;

  const startAutoScroll = (direction) => {
    stopAutoScroll();
    autoScrollInterval = setInterval(() => {
      if (direction === "next") {
        sliderInstance.slideNext(TRANSITION_SPEED);
        if (sliderInstance.isEnd) sliderInstance.slideTo(0, TRANSITION_SPEED);
      } else if (direction === "prev") {
        sliderInstance.slidePrev(TRANSITION_SPEED);
        if (sliderInstance.isBeginning) {
          sliderInstance.slideTo(sliderInstance.slides.length - 1, TRANSITION_SPEED);
        }
      }
    }, SCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    if (autoScrollInterval) clearInterval(autoScrollInterval);
  };

  sliderElement.addEventListener("mousemove", ({ clientX }) => {
    const { left, width } = sliderElement.getBoundingClientRect();
    const x = clientX - left;

    stopAutoScroll();

    if (x > width - SCROLL_THRESHOLD) {
      startAutoScroll("next");
    } else if (x < SCROLL_THRESHOLD) {
      startAutoScroll("prev");
    }
  });

  sliderElement.addEventListener("mouseleave", stopAutoScroll);
};

const init = (showViewedProducts, showRecommendedProducts, showStyledWithProducts) => (
  saveViewedProduct(),
  cacheElements(),
  renderPlaceholderContainers(state.elements.viewedProductsLoader, 4, 'product'),
  renderPlaceholderContainers(state.elements.styledWithProductsLoader, 4, 'product'),
  showViewedProducts && applyViewedProductsList()(),
  hideViewedProducts(),
  showRecommendedProducts && initRecommendedSections(),
  showStyledWithProducts && applyStyledWithProductsList()()
);

const cacheElements = () => {
  state.settings = {};
  state.settings.styledWithProducts = window?.productMetafields?.styledWith;
  const viewedProductsSection = document.querySelector('.recommendations--recent');
  const styledWithProductsSection = document.querySelector('.recommendations--styled');
  state.elements = {
    viewedProductsSection,
    styledWithProductsSection,
    viewedProductsLoader: viewedProductsSection?.querySelector('.recommendations__loader'),
    styledWithProductsLoader: styledWithProductsSection?.querySelector('.recommendations__loader')
  };
};

const hideViewedProducts = (forceHide) => {
  if (!window.viewedProductsSettings) return;
  if (state.viewedProductIds.length && !forceHide) return;
  state.elements.viewedProductsSection.style.display = 'none';
};

const readSectionSettings = (sectionEl) => {
  try {
    const settingsEl = sectionEl.querySelector('.recommendations__settings');
    return JSON.parse(settingsEl?.textContent || '{}');
  } catch (er) {
    console.error(er);
    return {};
  }
};

const getRecommendationProductId = () => (
  state.recommendationProductId ?? window.products?.[0]?.id ?? null
);

const fetchRecommendedSectionProducts = (
  { api_type, max_products_recommended },
  productId,
) => {
  if (!productId) return Promise.resolve([]);
  if (api_type === 'rebuy') {
    return fetchRebuyRecommendedProducts([productId], max_products_recommended);
  }
  const intent = api_type === 'shopify_complementary' ? 'complementary' : 'related';
  return fetchProductRecommendations(productId, max_products_recommended, intent);
};

// Each Recommended Products section initialises independently: its own settings
// (read from its own inline JSON), its own fetch (related / complementary /
// rebuy) and its own slider. The dataset guard makes the section's duplicated
// <script> include a harmless no-op, so multiple instances can coexist on a PDP.
const initRecommendedSection = (sectionEl) => {
  if (sectionEl.dataset.recommendationsInit) return;
  sectionEl.dataset.recommendationsInit = '1';

  const settings = readSectionSettings(sectionEl);
  const hideUntilLoaded = Boolean(settings.hide_skeleton);
  // Rebuilt on every load: the rendered-item map is keyed by product id and the
  // slider's loop config depends on how many products came back, so neither
  // survives a switch to a different product.
  let renderedItems = {};
  let slider = null;
  // A quick run through the colour swatches can resolve an older request after
  // a newer one. Only the most recent load is allowed to touch the DOM.
  let latestLoadId = 0;

  if (hideUntilLoaded) {
    // Hide the whole section (title, slider and padding) until products are
    // ready; no loading skeleton is shown.
    sectionEl.classList.add('hidden');
  } else {
    renderPlaceholderContainers(
      sectionEl.querySelector('.recommendations__loader'),
      4,
      'product',
    );
  }

  const teardownSlider = () => {
    // destroy() leaves the markup behind, and in loop mode that markup includes
    // Swiper's duplicated slides — clear the wrapper by hand.
    slider?.destroy(true, true);
    slider = null;
    renderedItems = {};
    const wrapper = sectionEl.querySelector('.recommendations__slider .swiper-wrapper');
    if (wrapper) wrapper.innerHTML = '';
  };

  const loadAndRender = async (productId) => {
    const loadId = ++latestLoadId;
    let products = [];
    try {
      products = (await fetchRecommendedSectionProducts(settings, productId)) || [];
    } catch (er) {
      console.error(er);
    }
    if (loadId !== latestLoadId) return;

    // Swap the old slides out only once the new ones are in hand, so switching
    // colour doesn't blank the row while the request is in flight.
    teardownSlider();

    if (!products.length) {
      sectionEl.classList.add('hidden');
      return;
    }
    // Reveal before Swiper measures so it reads the real slider width.
    sectionEl.classList.remove('hidden');
    slider = new Swiper(
      sectionEl.querySelector('.recommendations__slider'),
      buildSliderOptions(products.length),
    );
    try {
      await renderProducts(products, renderedItems, sectionEl, slider, true);
    } catch (er) {
      // Never let a render error leave the section stuck on its skeleton.
      console.error(er);
    }
    if (hideUntilLoaded) {
      // The slider's visibility is gated on the loader carrying `.hidden`
      // (normally toggled by createLazyAction, which we skip in this mode).
      sectionEl.querySelector('.recommendations__loader')?.classList.add('hidden');
    }
  };

  // Only reload a section that has already done its first load. One that is
  // still below the fold stays lazy and will pick up the current product when
  // it finally scrolls into view.
  let hasLoaded = false;
  const firstLoad = async () => {
    hasLoaded = true;
    await loadAndRender(getRecommendationProductId());
  };
  state.reloadRecommendedSections.push((productId) => {
    if (!hasLoaded) return;
    loadAndRender(productId);
  });

  // A display:none section can't trigger an IntersectionObserver, so in
  // hide-until-loaded mode we load eagerly; otherwise lazy-load on scroll.
  if (hideUntilLoaded) {
    firstLoad();
  } else {
    createLazyAction(sectionEl, firstLoad, '.recommendations__loader')();
  }
};

const onRecommendationProductChange = ({ detail }) => {
  const productId = detail?.id;
  if (!productId || productId === getRecommendationProductId()) return;
  state.recommendationProductId = productId;
  state.reloadRecommendedSections.forEach((reload) => reload(productId));
};

const initRecommendedSections = () => {
  state.recommendationProductId = window.products?.[0]?.id ?? null;
  document
    .querySelectorAll('.recommendations--main')
    .forEach(initRecommendedSection);
  // The section duplicates its <script> include per instance, so this module can
  // run more than once; only the run that actually claimed sections needs to
  // listen. The others would iterate an empty list.
  if (!state.reloadRecommendedSections.length) return;
  document.addEventListener(PRODUCT_CHANGED_EVENT, onRecommendationProductChange);
};

const applyStyledWithProductsList = () => (
  createLazyAction(
    state.elements.styledWithProductsSection,
    async () => (
      getStyledWithProducts(),
      initStyledWithSlider(),
      await renderProducts(
        state.styledWithProducts,
        state.renderedStyledWithItems,
        state.elements.styledWithProductsSection,
        state.styledWithSlider,
        false
      )
    ),
    '.recommendations__loader',  
  )
);

const getViewedProducts = async () => {
  if (!window.viewedProductsSettings) return;
  try {
    const savedProductIds = state.viewedProductIds.filter(({ id }) => (
      +id !== +state.currentProductId
    ));
    if (!savedProductIds.length) {
      hideViewedProducts(true);
      return;
    }
    state.viewedProducts = await fetchProductsById(savedProductIds);
    hideViewedProducts(!state.viewedProducts.length);
  } catch (er) {
    console.log(er);
    hideViewedProducts(true);
  }
};

const saveViewedProduct = () => {
  if (!window.viewedProductsSettings) return;
  state.viewedProductIds = JSON.parse(localStorage.getItem('viewed-products') || '[]');

  if (document.body.dataset.templateName !== 'product') return;

  state.currentProductId = document.querySelector('.Product').dataset.productId;

  const isCurrentProductAlreadyViewed = state.viewedProductIds.findIndex(({ id }) => (
    id === state.currentProductId?.toString()
  )) > -1;

  if (isCurrentProductAlreadyViewed) return;

  const newProductIdsList = [...state.viewedProductIds];
  const { max_products_saved } = window.viewedProductsSettings;

  if (state.viewedProductIds.length === max_products_saved) {
    newProductIdsList.pop();
  }

  newProductIdsList.unshift({ id: state.currentProductId });

  localStorage.setItem('viewed-products', JSON.stringify(newProductIdsList));
};

const applyViewedProductsList = () => (
  createLazyAction(
    state.elements.viewedProductsSection,
    async () => (
      await getViewedProducts(),
      initViewedProductsSlider(),
      await renderProducts(
        state.viewedProducts,
        state.renderedRecentItems,
        state.elements.viewedProductsSection,
        state.viewedProductsSlider,
      )
    ),
    '.recommendations__loader',
  )
);

const initStyledWithSlider = () => {
  const sliderElement = document.querySelector('.recommendations--styled .recommendations__slider');
  state.styledWithSlider = new Swiper(
    sliderElement,
    buildSliderOptions(state.styledWithProducts?.length || 0),
  );

  // enableAutoScrolling(sliderElement, state.styledWithSlider);
};

const initViewedProductsSlider = () => {
  const sliderElement = document.querySelector('.recommendations--recent .recommendations__slider');
  state.viewedProductsSlider = new Swiper(
    sliderElement,
    buildSliderOptions(state.viewedProducts.length),
  );

  // enableAutoScrolling(sliderElement, state.viewedProductsSlider);
};

const getStyledWithProducts = () => {
  if (state.settings.styledWithProducts) {
    state.styledWithProducts = restructureRestProducts(state.settings.styledWithProducts);
  } else {
    state.styledWithProducts = null;
    state.elements.styledWithProductsSection.classList.add('hidden');
  }
};

const renderProducts = async (
  products,
  renderedState,
  itemsContainer,
  slider,
  isRecommendedProdsSection,
) => {
  if (!products?.length) return;
  // Global-e currency conversion is best-effort: if its globals never load
  // (waitObjectProperty rejects after 5s) or a conversion throws, fall back to
  // default prices instead of letting the whole render reject — a rejected
  // render leaves the section stuck on its loading skeleton.
  let convertPrices = false;
  try {
    await waitObjectProperty("GLBE_PARAMS", window);
    if (window.GLBE_PARAMS.currencyCode !== "USD") {
      await waitObjectProperty("PriceInfo", window.GLBE_PARAMS);
      convertPrices = true;
    }
  } catch (er) {
    console.error('Global-e pricing unavailable; rendering default prices', er);
  }
  const productTemplates = products.map((product, index) => {
    const newProduct = {...product};
    if (convertPrices) {
      try {
        if (newProduct.compareAtPrice) {
          newProduct.compareAtPrice = glbeApp.modules.priceInfo.convertPrice(newProduct.compareAtPrice);
        }
        newProduct.price = glbeApp.modules.priceInfo.convertPrice(product.price);
      } catch (er) {
        console.error(er);
      }
    }
    const template = getProductItemBody(
      newProduct,
      index,
      renderedState,
      false,
      itemsContainer,
    );
    return template.replace('"product-item ', '"product-item swiper-slide ');
  });
  slider.appendSlide(productTemplates);
  attachProductItemEvents(
    renderedState,
    itemsContainer,
    isRecommendedProdsSection,
  );
  // Videos / labels are an enhancement — never let them block the reveal.
  try {
    await renderVideos(products, renderedState);
  } catch (er) {
    console.error(er);
  }
  slider.update();
};

onDocumentReady(() => init(false, true, false));
